// @ts-check

import { atom, useAtomValue, useSetAtom } from "jotai"
import { useMemo, useRef } from "react"
import { useSelector } from "react-redux"
import z from "zod"
import { ActiveStreamRecordSchema, ChatStreamMetaSchema, ReportStreamMetaSchema, StreamContentChunkSchema, StreamMetaSchema, StreamStructuredContentChunkSchema, AgentChatChunkDataContentSchema, EditChatStreamMetaSchema, RegenerateChatStreamMetaSchema } from "../schema"
import { useGetLatestUserToken } from "../utils"
import { ChatProviderError, ErrorCodes, StreamEvents } from "../api"
import { ChatSessionQueryKeys, useActiveChatSessionId } from "../sessions"
import { ChatMessageQueryKeys, ChatMessageQueryActions } from "../messages/query"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { ChatStreamMutationOptions } from "./query"
import { handleAgentStreamEvent, useStreamMutation } from "./utils"
import { AgentMode, AI_USER, AGENT_USER, DEEP_RESEARCH_USER } from "../utils"
import { clearReviewProgress } from "../canvas/reviewProgress"
import { store } from "@/lib/store"

const activeStreamsAtom = atom(/**@type{Record<string, z.infer<typeof ActiveStreamRecordSchema>>}*/({}))
/** @typedef {typeof activeStreamsAtom extends import("jotai").PrimitiveAtom<infer T> ? T : never} ActiveStreams */

function useActiveSessionHasActiveStream() {
  const [activeSessionId] = useActiveChatSessionId()
  const sessionHasStreamAtom = useMemo(() => (
    atom(get => get(activeStreamsAtom)[activeSessionId] != null)
  ), [activeSessionId])

  const hasStream = useAtomValue(sessionHasStreamAtom)
  return hasStream
}

/** @satisfies {Record<string, (...args: any[]) => (activeStreams: ActiveStreams) => ActiveStreams>} */
const ActiveStreamsAtomActions = {
  /**
   * @param {string} sessionId
   * @param {Partial<z.infer<typeof ChatStreamMetaSchema>> | null} streamMeta
   * @param {"content" | "structured-content"} [responseContentType]
   */
  SETUP_CHAT_STREAM(sessionId, streamMeta, responseContentType) {
    return streams => ({
      ...streams,
      [sessionId]: {
        type: "chat",
        query: streamMeta == null ? null : {
          message: streamMeta.message,
          images: streamMeta.images,
          files: streamMeta.files,
          mode: streamMeta.mode,
        },
        response: (() => {
          if(responseContentType == null) return null
          if(responseContentType === "content") {
            return { type: responseContentType, content: "" }
          }
          if(responseContentType === "structured-content") {
            return { type: responseContentType, content: /**@type{*[]}*/([]) }
          }
          throw new Error(`Unknown response content type: ${responseContentType}`)
        })(),
        timestamp: new Date().toISOString(),
      },
    })
  },
  /**
   * @param {string} sessionId
   * @param {z.infer<typeof ReportStreamMetaSchema> | null} streamMeta
   * @param {"content" | "structured-content"} [responseContentType]
   */
  SETUP_REPORT_STREAM(sessionId, streamMeta, responseContentType) {
    return streams => ({
      ...streams,
      [sessionId]: {
        type: "report",
        query: streamMeta == null ? null : {
          modality: streamMeta.modality,
          patientInfo: streamMeta.patientInfo,
          image: streamMeta.image,
        },
        response: (() => {
          if(responseContentType == null) return null
          if(responseContentType === "content") {
            return { type: responseContentType, content: "" }
          }
          if(responseContentType === "structured-content") {
            return { type: responseContentType, content: /**@type{*[]}*/([]) }
          }
          throw new Error(`Unknown response content type: ${responseContentType}`)
        })(),
        timestamp: new Date().toISOString(),
      },
    })
  },
  /**
   * @template {"content" | "structured-content"} T
   * @param {string} sessionId
   * @param {T} responseContentType
   * @param {T extends "content" ? string : T extends "structured-content" ? *[]: never} content
   */
  APPEND_RESPONSE_CHUNK(sessionId, responseContentType, content) {
    return streams => (
      Object.fromEntries(
        Object.entries(streams).map(entry => {
          const [streamSessionId, stream] = entry
          if (streamSessionId !== sessionId) return entry
          if (stream == null) return entry
          if (stream.response != null && stream.response.type !== responseContentType) return entry

          return [streamSessionId, {
            ...stream,
            response: {
              ...stream.response,
              type: responseContentType,
              content: (() => {
                if(responseContentType === "content") {
                  // @ts-ignore
                  return (stream.response?.content ?? "") + content
                }
                if(responseContentType === "structured-content") {
                  return [...(stream.response?.content ?? []), content]
                }
                throw new Error(`Unknown response content type: ${responseContentType}`)
              })(),
            }
          }]
        })
      )
    )
  },
  /** @param {string} sessionId */
  DELETE_STREAM(sessionId) {
    return streams => ({
      ...streams,
      [sessionId]: undefined,
    })
  },
}

/** @param {string} sessionId */
function useStreamingMessage(sessionId) {
  const { user } = useSelector((state) => state.app.datastate)
  const sessionStreamRecordAtom = useMemo(() => (
    atom(get => get(activeStreamsAtom)[sessionId])
  ), [sessionId])

  const streamRecord = useAtomValue(sessionStreamRecordAtom)
  if(streamRecord == null) return { query: null, response: null }

  const queryMessage = (() => {
    if(streamRecord.query == null) return null
    if (streamRecord.type === "chat" && streamRecord.query.message == null) return null

    const queryText = (() => {
      if (streamRecord.type === "report") return "Generate a report for this image"
      if (streamRecord.type === "chat") return streamRecord.query.message
      return ""
    })()
    const queryFiles = (() => {
      if (streamRecord.type === "chat") return streamRecord.query.files ?? []
      return []
    })()

    return /** @satisfies {ChatMessage} */({
      user: user?._id,
      text: queryText,
      images: streamRecord.query.images,
      files: queryFiles,
      timestamp: streamRecord.timestamp,
    })
  })()

  const responseMessage = (() => {
    if(streamRecord.response == null) return null

    let responseUser = AI_USER
    if(streamRecord.type === "chat" && (
      streamRecord.query.mode === AgentMode.AGENT ||
      streamRecord.query.mode === AgentMode.WEB_SEARCH ||
      streamRecord.query.mode === AgentMode.MOREMI_CO_RESEARCHER ||
      streamRecord.query.mode === AgentMode.CANVAS_AGENT
    )) {
      responseUser = AGENT_USER
    }
    if(streamRecord.type === "chat" && streamRecord.query.mode === AgentMode.DEEP_RESEARCH) {
      responseUser = DEEP_RESEARCH_USER
    }

    let responseText = ""
    if(streamRecord.type === "chat" && streamRecord.response.type === "content") {
      responseText = streamRecord.response.content
    }

    /** @type {ChatMessagePart[]} */
    let responseParts = []
    if(streamRecord.type === "chat" && streamRecord.response.type === "structured-content") {
      responseParts = streamRecord.response.content
    }

    let reponseReport
    if(streamRecord.type === "report") {
      const [content, streamStatuses] = (() => {
        try {
          if (streamRecord.response.type === "content") {
            return [streamRecord.response.content, /** @type {string[]} */([])]
          }

          let _content = ""
          /** @type {string[]} */
          let _streamStatuses = []
          for (const chunk of streamRecord.response.content) {
            if (chunk.event === "intermediate") {
              _streamStatuses.push(chunk.data.content)
            }
            if (chunk.event === "report") {
              _content += chunk.data.content
            }
          }
          return [_content, _streamStatuses]
        } catch (error) {
          return ["", /** @type {string[]} */([])]
        }
      })()

      reponseReport = {
        original: content,
        current: content,
        streamStatuses,
        modality: streamRecord.query.modality,
        patientInfo: streamRecord.query.patientInfo,
        chatSessionId: sessionId,
        image: streamRecord.query.image,
      }
    }

    return /** @satisfies {ChatMessage} */({
      user: responseUser,
      text: responseText,
      message: responseParts,
      meta: streamRecord.type === "chat" ? { mode: streamRecord.query.mode } : undefined,
      report: reponseReport,
      timestamp: streamRecord.timestamp,
    })
  })()

  return {
    query: queryMessage,
    response: responseMessage,
  }
}

/** @param {string} sessionId */
function useStreamingReport(sessionId) {
  const sessionStreamRecordAtom = useMemo(() => (
    atom(get => get(activeStreamsAtom)[sessionId])
  ), [sessionId])

  const streamRecord = useAtomValue(sessionStreamRecordAtom)
  if(streamRecord == null) return null
  if(streamRecord.type !== "report") return null

  const [content, streamStatuses] = (() => {
    try {
      if(streamRecord.response.type === "content") {
        return [streamRecord.response.content, /** @type {string[]} */([])]
      }

      let _content = ""
      /** @type {string[]} */
      let _streamStatuses = []
      for(const chunk of streamRecord.response.content) {
        if(chunk.event === "intermediate") {
          _streamStatuses.push(chunk.data.content)
        }
        if(chunk.event === "report") {
          _content += chunk.data.content
        }
      }
      return [_content, _streamStatuses]
    } catch (error) {
      return ["", /** @type {string[]} */([])]
    }
  })()

  return {
    original: content,
    current: content,
    streamStatuses,
    modality: streamRecord.query.modality,
    patientInfo: streamRecord.query.patientInfo,
    chatSessionId: sessionId,
    image: streamRecord.query.image,
  }
}

/**
 * @typedef {Object} StreamMetaRecord
 * @property {ChatMessage[]} [removedMessages]
 */

const streamMetaAtom = atom(/** @type {Record<string, StreamMetaRecord>} */({}))

function useGetChatSessionStream() {
  const getUserToken = useGetLatestUserToken()
  const setActiveStreams = useSetAtom(activeStreamsAtom)
  const setStreamMeta = useSetAtom(streamMetaAtom)
  const queryClient = useQueryClient()
  // Sessions where this check found a live stream. A check that found none
  // must not delete the stream of a message sent while it was running.
  const resumedSessionsRef = useRef(new Set())

  /** @param {import("../api").StreamEventsPayload} payload */
  function handleStreamEvent(payload) {
    switch(payload.event) {
      case StreamEvents.STREAM_STARTED: {
        resumedSessionsRef.current.add(payload.data.sessionId)
        // The server replays the stream from its start.
        clearReviewProgress(payload.data.sessionId)

        if (payload.data.meta == null) {
          console.warn("Stream started without metadata")
          break;
        }

        try {
          const meta = z.union([
            EditChatStreamMetaSchema,
            RegenerateChatStreamMetaSchema,
            ChatStreamMetaSchema,
          ]).parse(payload.data.meta)

          const responseContentType = (
            payload.data.user === AGENT_USER ||
            payload.data.user === DEEP_RESEARCH_USER
          ) ? "structured-content" : "content"

          const editMeta = EditChatStreamMetaSchema.safeParse(payload.data.meta)
          if(editMeta.success) {
            const messagesData = queryClient.getQueryData(
              ChatMessageQueryKeys.CHAT_MESSAGES(payload.data.sessionId)
            )
            if (messagesData == null) return

            const { data: newMessagesData, removed } = ChatMessageQueryActions.TRUNCATE_FROM(
              editMeta.data.editedMessageId
            )(messagesData)

            if (removed.length === 0) return

            const editedMessage = removed[0]
            const editedMessageResponse = removed[1]

            const responseContentType = (() => {
              if (editedMessageResponse == null) return "content"
              if (editedMessageResponse?.user !== AI_USER) return "structured-content"
              return "content"
            })()

            const editedMessageMode = (() => {
              if (editedMessageResponse == null) return AgentMode.DEFAULT
              if (editedMessageResponse?.user === AGENT_USER) return AgentMode.AGENT
              if (editedMessageResponse?.user === DEEP_RESEARCH_USER) return AgentMode.DEEP_RESEARCH
              return AgentMode.DEFAULT
            })()

            queryClient.setQueryData(
              ChatMessageQueryKeys.CHAT_MESSAGES(payload.data.sessionId),
              newMessagesData
            )
            setActiveStreams(
              ActiveStreamsAtomActions.SETUP_CHAT_STREAM(payload.data.sessionId, {
                message: editMeta.data.message,
                images: editedMessage.images,
                files: editedMessage.files,
                mode: editedMessageMode,
              }, responseContentType)
            )
            setStreamMeta(prev => ({
              ...prev,
              [payload.data.sessionId]: {
                ...prev[payload.data.sessionId],
                removedMessages: removed,
              },
            }))
            return
          }

          const regenerateMeta = RegenerateChatStreamMetaSchema.safeParse(payload.data.meta)
          if(regenerateMeta.success) {
            const messagesData = queryClient.getQueryData(
              ChatMessageQueryKeys.CHAT_MESSAGES(payload.data.sessionId)
            )
            if (messagesData == null) return

            const { data: newMessagesData, removed } = ChatMessageQueryActions.TRUNCATE_FROM(
              regenerateMeta.data.regeneratedMessageId
            )(messagesData)

            if (removed.length === 0) return

            const regeneratedMessage = removed[0]

            const responseContentType = (() => {
              if (regeneratedMessage?.user !== AI_USER) return "structured-content"
              return "content"
            })()

            const regeneratedMessageMode = (() => {
              if (regeneratedMessage?.user === AGENT_USER) return AgentMode.AGENT
              if (regeneratedMessage?.user === DEEP_RESEARCH_USER) return AgentMode.DEEP_RESEARCH
              return AgentMode.DEFAULT
            })()

            queryClient.setQueryData(
              ChatMessageQueryKeys.CHAT_MESSAGES(payload.data.sessionId),
              newMessagesData
            )
            setActiveStreams(
              ActiveStreamsAtomActions.SETUP_CHAT_STREAM(
                payload.data.sessionId,
                { mode: regeneratedMessageMode },
                responseContentType,
              )
            )
            setStreamMeta(prev => ({
              ...prev,
              [payload.data.sessionId]: {
                ...prev[payload.data.sessionId],
                removedMessages: removed,
              },
            }))
          }

          const chatMeta = ChatStreamMetaSchema.safeParse(payload.data.meta)
          if(chatMeta.success) {
            setActiveStreams(
              ActiveStreamsAtomActions.SETUP_CHAT_STREAM(
                payload.data.sessionId,
                meta,
                responseContentType
              )
            )
            return
          }

          break;
        } catch (chatParseError) {
          try {
            const reportMeta = ReportStreamMetaSchema.parse(payload.data.meta)

            setActiveStreams(
              ActiveStreamsAtomActions.SETUP_REPORT_STREAM(
                payload.data.sessionId,
                reportMeta
              )
            )
            break;
          } catch (reportParseError) {
            console.warn("Failed to parse stream metadata", {
              meta: payload.data.meta,
              chatError: chatParseError,
              reportError: reportParseError
            })
          }
        }
        break;
      }
      case StreamEvents.STREAM_CONTENT: {
        let chunk
        try {
          chunk = StreamContentChunkSchema.parse(payload.data.chunk)
        } catch (error) {
          console.warn("Failed to parse chat stream content event data", error)
          return
        }

        setActiveStreams(
          ActiveStreamsAtomActions.APPEND_RESPONSE_CHUNK(
            payload.data.sessionId,
            "content",
            chunk.data.content
          )
        )
        break;
      }
      case StreamEvents.STREAM_STRUCTURED_CONTENT: {
        let chunk
        try {
          chunk = StreamStructuredContentChunkSchema.parse(payload.data.chunk)
        } catch (error) {
          console.warn("Failed to parse stream structured content event data", error)
          return
        }

        if (chunk.type === "chat") {
          try {
            const eventData = AgentChatChunkDataContentSchema.parse(chunk.data.content)
            handleAgentStreamEvent(eventData, payload.data.sessionId)
          } catch (error) {
            console.warn("Failed to parse agent event data", error)
          }
        } else if (chunk.type === "report") {
          setActiveStreams(
            ActiveStreamsAtomActions.APPEND_RESPONSE_CHUNK(
              payload.data.sessionId,
              "structured-content",
              {
                event: chunk.data.content.event,
                data: chunk.data.content,
              }
            )
          )
        }
        break;
      }
      case StreamEvents.STREAM_ENDED: {
        break;
      }
      default:
        break;
    }
  }

  const mutationOptions = ChatStreamMutationOptions.GET_CHAT_STREAM({
    getUserToken,
    onStreamEvent: handleStreamEvent,
  })

  return useStreamMutation(mutationOptions, {
    onError(error, variables, mutateResult, context) {
      const streamMeta = store.get(streamMetaAtom)
      const removedMessages = streamMeta[variables.sessionId]?.removedMessages || []
      if (removedMessages.length <= 0) return

      context.client.setQueryData(
        ChatMessageQueryKeys.CHAT_MESSAGES(variables.sessionId),
        ChatMessageQueryActions.RESTORE_MESSAGES(removedMessages)
      )
    },
    onSettled: (data, error, variables) => {
      if (!resumedSessionsRef.current.delete(variables.sessionId)) return

      setActiveStreams(ActiveStreamsAtomActions.DELETE_STREAM(variables.sessionId))
      setStreamMeta(prev => ({
        ...prev,
        [variables.sessionId]: undefined,
      }))
    }
  }, {
    suppressErrors: error => {
      if(error == null) return false
      return error instanceof ChatProviderError && error.code === ErrorCodes.NOT_FOUND
    }
  })
}

function useCancelActiveChatStream() {
  const [activeSessionId] = useActiveChatSessionId()
  const { user } = useSelector((state) => state.app.datastate)

  return useMutation(ChatStreamMutationOptions.CANCEL_CHAT_STREAM({
    sessionId: activeSessionId,
    userToken: user?.userToken
  }))
}

export * from "./query"
export * from "./utils"
export {
  activeStreamsAtom,
  ActiveStreamsAtomActions,

  useActiveSessionHasActiveStream,
  useStreamingMessage,
  useStreamingReport,

  useGetChatSessionStream,
  useCancelActiveChatStream,
}
